//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
// focus-time-meter imports
import { IProps } from "./FocusTimeMeter.d";
import * as s from "./FocusTimeMeter.styles";
// other internals
import { SegmentedBar } from "@components/segmented-bar";
import { isFocusTimeCrawledStatusSynced } from "@util/team-health.util";
// material-ui
// import { Button } from '@material-ui/core';

//////////////////
// COMPONENT
//////////////////
export class FocusTimeMeter extends React.PureComponent<IProps> {
  private getRoundedHours = (minutes: number) => {
    if (minutes > 600) {
      // if over 10, round to nearest integer
      return Math.round(minutes / 60);
    } else {
      // otherwise round to the nearest .5
      return Math.round(minutes / 30) / 2;
    }
  };

  private renderLabel = () => {
    const { focusMinutes, goalMinutes, totalMinutes, noLabel } = this.props;
    if (!focusMinutes || !totalMinutes || noLabel) {
      return <span />;
    }

    const percentile = (focusMinutes - goalMinutes) / (totalMinutes - goalMinutes);

    if (focusMinutes < goalMinutes) {
      return <div style={s.badLabel}>Low</div>;
    } else if (percentile < 0.3) {
      return <div style={s.goodLabel}>Ok</div>;
    } else if (percentile < 0.6) {
      return <div style={s.goodLabel}>Good</div>;
    } else {
      return <div style={s.goodLabel}>Great</div>;
    }
  };

  private getSegments = (isCrawled: boolean) => {
    const { focusMinutes, busyMinutes, totalMinutes } = this.props;
    const focusMins = isCrawled && typeof focusMinutes === "number" ? focusMinutes : 0;
    const busyMins = isCrawled && typeof busyMinutes === "number" ? busyMinutes : 0;

    const remainder = totalMinutes ? totalMinutes - focusMins - busyMins : 1;
    const focusLabel = this.getRoundedHours(focusMins) + " hr Focus Time";
    const busyLabel = this.getRoundedHours(busyMins) + " hr meetings";

    return [
      { color: s.focusColor, value: focusMins, tipLabel: focusLabel },
      { color: s.busyColor, value: busyMins, tipLabel: busyLabel },
      { color: s.barBg, value: remainder, isInset: true },
    ];
  };

  public renderUnCrawledLegend = (
    crawledStatus: Omit<
      ISchema.CrawledStatus,
      ISchema.CrawledStatus.Synced | ISchema.CrawledStatus.Stale
    >,
  ) => {
    let msg = "Processing. Please check back.";
    switch (crawledStatus) {
      case ISchema.CrawledStatus.None:
        msg = `No data yet.`;
        break;
      case ISchema.CrawledStatus.NoAccess:
      case ISchema.CrawledStatus.VeryStale:
        msg = `Sorry, Clockwise does not have access to this calendar.`;
        break;
      case ISchema.CrawledStatus.Pending:
        msg = "Clockwise is processing this calendar. Please check back.";
      case ISchema.CrawledStatus.Stale: // Both of these should not be accessed.
      case ISchema.CrawledStatus.Synced:
      default:
        break;
    }

    return (
      <div style={s.legendRow}>
        <div style={s.uncrawledMsg}>{msg}</div>
      </div>
    );
  };

  private renderCrawledLegend = (focusMinutes: number) => {
    return (
      <div style={s.legendRow}>
        <div style={s.focusDot} />
        <div style={s.focusHours}>{this.getRoundedHours(focusMinutes)} hr</div>
        <div>Focus Time</div>
        {this.renderLabel()}
      </div>
    );
  };

  public render() {
    const { focusMinutes, goalMinutes, totalMinutes, crawledStatus, noLabel } = this.props;
    const isCrawled = isFocusTimeCrawledStatusSynced(crawledStatus);

    const goalPercent = !!totalMinutes ? (goalMinutes / totalMinutes) * 100 : 0;
    const goalStyle = { ...s.goalIndicator, left: `${goalPercent}%` };

    return (
      <div style={s.container}>
        {isCrawled && typeof focusMinutes === "number"
          ? this.renderCrawledLegend(focusMinutes)
          : this.renderUnCrawledLegend(crawledStatus)}
        <div style={s.barContainer}>
          {!noLabel ? <div style={goalStyle} /> : null}
          <SegmentedBar segments={this.getSegments(isCrawled)} animateIn />
        </div>
      </div>
    );
  }
}
